import { Popover, PopoverButton, PopoverPanel } from "@headlessui/react";
import { useNavigate } from "@tanstack/react-router";
import { FaceContent, X } from "@untitled-ui/icons-react";
import classNames from "classnames";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import Button from "@/components/forms/Button";
import Checkbox from "@/components/forms/Checkbox";
import Field from "@/components/forms/Field";
import Label from "@/components/forms/Label";
import Radio from "@/components/forms/Radio";
import RadioGroup from "@/components/forms/RadioGroup";
import Text from "@/components/text/Text";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { useLayoutContext } from "@/layouts/LayoutContext";

import { useCreateMoodUser, useFetchMoods } from "../services";
import { type Mood } from "../types";

export default function MoodForm() {
  const { t } = useTranslation("mood.user");
  const navigation = useNavigate();
  const { messengerBottomOffset, messengerCompactEdge } = useLayoutContext();
  const isDesktop = useMediaQuery("(min-width: 640px)");
  const bottomPx = isDesktop
    ? messengerCompactEdge + 4
    : 16 + messengerBottomOffset;

  const [selectedMood, setSelectedMood] = useState<Mood<true>>();
  const [selectedReasonIds, setSelectedReasonIds] = useState<number[]>([]);

  const { data: moods } = useFetchMoods();

  const { mutate: create, isPending } = useCreateMoodUser();

  const onSubmit = () => {
    if (!selectedMood) return;

    create(
      {
        mood_id: selectedMood.id,
        reasons: selectedReasonIds,
      },
      {
        onSuccess: () => {
          navigation({
            to: "/",
          });
        },
      },
    );
  };

  if (!moods || moods.length === 0) {
    return null;
  }

  return (
    <Popover>
      <PopoverButton
        className="bg-primary-700 fixed end-4 z-50 rounded-full p-2 print:hidden"
        style={{ bottom: `${bottomPx}px` }}
      >
        <div className="border-primary-600 bg-primary-600 rounded-full border p-2">
          <FaceContent className="size-6 text-white" />
        </div>
      </PopoverButton>
      <PopoverPanel
        transition
        anchor={{
          to: "top end",
          gap: 12,
        }}
        className="border-primary-500 ring-primary-100 origin-top-end z-50 w-80 rounded-2xl border bg-white p-6 shadow-xs ring-4 transition duration-200 ease-in-out data-closed:-translate-y-1 data-closed:opacity-0 print:hidden"
      >
        {({ close }) => (
          <div className="flex flex-col gap-4 select-none">
            <div className="flex items-center justify-between">
              <Text color="gray" weight="bold">
                {selectedMood
                  ? t("What is your mood today?")
                  : t("How is your mood today?")}
              </Text>
              <Button size="sm" square color="tertiary-gray">
                <X className="size-5" onClick={() => close()} />
              </Button>
            </div>
            {selectedMood ? (
              <div className="flex flex-1 flex-col gap-2">
                {selectedMood.reasons.map((reason) => {
                  const checked = selectedReasonIds.includes(reason.id);
                  return (
                    <Field key={reason.id} inline>
                      <div
                        className={classNames(
                          "relative flex flex-1 cursor-pointer items-center gap-2 rounded-lg border px-2 py-3",
                          "hover:border-primary-200 hover:bg-primary-50",
                          checked && "border-primary-200 bg-primary-50",
                        )}
                        onClick={() =>
                          setSelectedReasonIds(
                            !checked
                              ? [...selectedReasonIds, reason.id]
                              : selectedReasonIds.filter(
                                  (id) => id !== reason.id,
                                ),
                          )
                        }
                        key={reason.id}
                      >
                        <Checkbox
                          checked={checked}
                          size="sm"
                          className="peer"
                        />
                        <Label className="cursor-pointer text-sm font-bold text-gray-700">
                          {reason.reason}
                        </Label>
                      </div>
                    </Field>
                  );
                })}
                <Button isLoading={isPending} onClick={() => onSubmit()}>
                  {t("Submit", { ns: "core.shared" })}
                </Button>
              </div>
            ) : (
              <RadioGroup
                value={selectedMood}
                onChange={(mood) => setSelectedMood(mood)}
              >
                <div className="flex flex-1 flex-col gap-2">
                  {moods.map((mood) => (
                    <Field key={mood.id} inline>
                      <div
                        className={classNames(
                          "relative flex flex-1 cursor-pointer items-center gap-2 rounded-lg border px-2 py-3",
                          "hover:border-primary-200 hover:bg-primary-50",
                        )}
                        onClick={() => setSelectedMood(mood)}
                        key={mood.id}
                      >
                        <Radio value={mood} size="sm" />
                        <Label className="cursor-pointer text-sm font-medium">
                          {mood.name}
                        </Label>
                        <div className="ms-auto text-sm">{mood.emoji}</div>
                      </div>
                    </Field>
                  ))}
                </div>
              </RadioGroup>
            )}
          </div>
        )}
      </PopoverPanel>
    </Popover>
  );
}
